import { Injectable } from '@angular/core';
import {
  deleteField,
  doc,
  Firestore,
  setDoc,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { getDownloadURL } from '@angular/fire/storage';
import { getVideoAttributesMapping } from '@yoimo/client-sdk/channels';
import {
  AttributeKey,
  AttributeKeys,
  CustomAttributes,
  FilteredVideoListModule,
  FiltersAttributeDefinition,
  VideoAttribute,
  VideoAttributesMapping,
  VideoAttributeValue,
} from '@yoimo/interfaces';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import {
  AddedAttributesType,
  AttributeProps
} from '../interfaces/attribute';
import { EditChannel, SourceDocuments } from './channels.service';
import { ConsentTermsService } from './consent-terms.service';
import { Upload, UploadService } from './upload.service';

export interface AttributeSetupResult {
  addedAttributes: AddedAttributesType[];
  selectedAttributeValueKeys: string[];
  currentAttributes: CustomAttributes;
  originalAttributes?: CustomAttributes;
}

export interface MappedFilter {
  label: string;
  source?: string;
  isAttribute: boolean;
  optionType: 'Group' | 'Value';
  imageUrl: string | null;
  selectedOptionIds?: string[];
}

export interface BuiltAddedAttributes {
  addedAttributes: AddedAttributesType[];
  selectedAttributeValueKeys: string[];
}

export const ACCEPTED_IMAGE_SIZE = 1 * 1024 * 1024;
export const ACCEPTED_ICON_TYPE = ['image/png', 'image/jpeg'];

@Injectable({
  providedIn: 'root',
})
export class VideoAttributesService {
  imageUpload!: Upload;
  videoAttributes: VideoAttributesMapping['mappings'] = {};
  searchText = '';
  videoSourceDocs: SourceDocuments['videoSourceDocs'] = [];
  filteredVideos: SourceDocuments['videoSourceDocs'] = [];
  sourceDocs: SourceDocuments = {
    videoSourceDocs: [],
    tagSourceDocs: [],
    eventSourceDocs: [],
  };
  filterList?: Record<string, string>[];

  private readonly saveStatusSubject = new Subject<boolean>();
  private readonly saveStatusInStream = new Subject<boolean>();
  
  startSave(): void { this.saveStatusSubject.next(true); }
  streamSave(): void { this.saveStatusInStream.next(true); }
  endSave(): void { this.saveStatusSubject.next(false); }
  resetStreamSaveStatus(): void { this.saveStatusInStream.next(false); }
  getSaveStatus(): Observable<boolean> { return this.saveStatusSubject.asObservable(); }
  getStreamSaveStatus(): Observable<boolean> { return this.saveStatusInStream.asObservable(); }

  constructor(
    private readonly functions: Functions,
    private readonly firestore: Firestore,
    private readonly uploadService: UploadService,
    private readonly consentTerms: ConsentTermsService
  ) {}

  searchVideos(): void {
    if (this.searchText.trim()) {
      this.filteredVideos = this.videoSourceDocs.filter((video) =>
        video['name'].toLowerCase().includes(this.searchText.toLowerCase()),
      );
    } else {
      this.filteredVideos = this.videoSourceDocs;
    }
  }

  searchEvents(): void {
    this.filterList = this.sourceDocs.eventSourceDocs.filter(
      (event) =>
        event['name']
          ?.toString()
          ?.toLowerCase()
          .indexOf(this.searchText.toLowerCase()) !== -1,
    );
  }

  searchTags(): void {
    this.filterList = this.sourceDocs.tagSourceDocs.filter(
      (tag) =>
        tag['name']
          ?.toString()
          ?.toLowerCase()
          .indexOf(this.searchText.toLowerCase()) !== -1,
    );
  }


  resetSearch(): void {
    this.searchText = '';
    this.filteredVideos = this.videoSourceDocs;
    this.filterList = undefined;
  }

  syncSourceDocs(sourceDocs: SourceDocuments): void {
    this.sourceDocs = sourceDocs;
    this.videoSourceDocs = sourceDocs.videoSourceDocs;
    this.filteredVideos = sourceDocs.videoSourceDocs;
  }

  getAttributeName(attr: string, videoAttributes: VideoAttributesMapping): string {
    return videoAttributes.mappings[attr as AttributeKey]?.label || '';
  }

  hasInvalidAttributes(addedAttributes: AddedAttributesType[]): boolean {
    return addedAttributes.some(
      (attr: AddedAttributesType) =>
        (attr.selectedValues.attributeName && !attr.selectedValues.attributeValue) ||
        (!attr.selectedValues.attributeName && attr.selectedValues.attributeValue)
    );
  }

  setupAttributes(
    videoAttributes: VideoAttributesMapping,
    defaultAttribute: CustomAttributes,
    sourceData?: Partial<EditChannel>
  ): AttributeSetupResult {
    if (!defaultAttribute || typeof defaultAttribute !== 'object' || (sourceData && typeof sourceData !== 'object')) {
      return {
        addedAttributes: [],
        selectedAttributeValueKeys: [],
        currentAttributes: {},
      };
    }

    const fetchedData: CustomAttributes = {};
    const existingAttributeKeys = Object.keys(defaultAttribute) as AttributeKey[];

    existingAttributeKeys.forEach((attr) => {
      const attributeValue = defaultAttribute[attr];
      if (videoAttributes.mappings?.[attr]) {
        const attributeMapping = videoAttributes.mappings[attr];
        if (attributeValue && attributeValue.trim() !== '') {
          const valueExists = attributeMapping?.values?.some(
            (item: VideoAttributeValue) => item.name === attributeValue
          );
          if (valueExists) {
            fetchedData[attr] = attributeValue;
          } else {
            fetchedData[attr] = '';
          }
        } else {
          fetchedData[attr] = '';
        }
      }
    });

    const currentAttributes = { ...fetchedData };
    const selectedAttributeValueKeys: string[] = [];
    const configuredAttributes = Object.keys(fetchedData).sort((a, b) => a.localeCompare(b)) as AttributeKey[];
    const addedAttributes: AddedAttributesType[] = configuredAttributes.map((attributeName: AttributeKey) => {
      const attributeMapping = videoAttributes.mappings?.[attributeName];
      const attributeValue = fetchedData[attributeName] || '';
      const matchingValue = attributeValue
        ? attributeMapping?.values?.find((item) => item.name === attributeValue)
        : undefined;

      selectedAttributeValueKeys.push(matchingValue?.label || attributeValue || '');

      return {
        attributeList: [],
        selectedValues: { attributeName, attributeValue },
        valuesList: [],
      };
    });

    if (videoAttributes?.mappings) {
      const allAttributes = (
        Object.keys(videoAttributes.mappings).sort((a, b) => a.localeCompare(b)) as AttributeKey[]
      ).map((item) => ({ key: this.getAttributeName(item, videoAttributes), value: item }));

      addedAttributes.forEach((attribute) => {
        const selectedAttributes = new Set(addedAttributes.map((a) => a.selectedValues.attributeName));

        attribute.attributeList = allAttributes.filter(
          (attr) => !selectedAttributes.has(attr.value)
        );
        attribute.attributeList.push({
          key: this.getAttributeName(attribute.selectedValues.attributeName as AttributeKey, videoAttributes),
          value: attribute.selectedValues.attributeName,
        });

        if (attribute.selectedValues.attributeName) {
          const attributeName = attribute.selectedValues.attributeName as AttributeKey;
          const attributeMapping = videoAttributes.mappings?.[attributeName];
          attribute.valuesList =
            attributeMapping?.values?.map((item: VideoAttributeValue) => ({
              key: item?.label || item.name,
              value: item.name,
            })) || [];
        }
      });
    }

    return { addedAttributes, selectedAttributeValueKeys, currentAttributes };
  }

  buildAddedAttributes(
    videoAttributes: VideoAttributesMapping,
    attributes: CustomAttributes,
  ): BuiltAddedAttributes {
    const fetchedData: CustomAttributes = {};

    AttributeKeys.forEach((attr) => {
      const mapping = videoAttributes?.mappings?.[attr];
      const selectedValue = attributes?.[attr];

      if (
        mapping &&
        selectedValue &&
        mapping.values.some((item) => item.name === selectedValue)
      ) {
        fetchedData[attr as AttributeKey] = selectedValue;
      }
    });

    const selectedAttributeValueKeys: string[] = [];

    const addedAttributes: AddedAttributesType[] = (
      Object.keys(fetchedData).sort((a, b) => a.localeCompare(b)) as AttributeKey[]
    ).map((attributeName: AttributeKey) => {
      const matchedValue =
        videoAttributes.mappings[attributeName]?.values.find(
          (item) => item.name === fetchedData[attributeName],
        );

      selectedAttributeValueKeys.push(
        matchedValue?.label || fetchedData[attributeName] || '',
      );

      return {
        attributeList: [],
        selectedValues: {
          attributeName,
          attributeValue: fetchedData[attributeName] || '',
        },
        valuesList: [],
      };
    });

    const allAttributes = (
      Object.keys(videoAttributes?.mappings || {}).sort((a, b) => a.localeCompare(b)) as AttributeKey[]
    ).map((item) => ({
      key: this.getAttributeName(item, videoAttributes),
      value: item,
    }));

    const selectedAttributes = new Set(
      addedAttributes.map((a) => a.selectedValues.attributeName),
    );

    addedAttributes.forEach((attribute) => {
      attribute.attributeList = allAttributes.filter(
        (attr) => !selectedAttributes.has(attr.value),
      );

      attribute.attributeList.push({
        key: this.getAttributeName(
          attribute.selectedValues.attributeName as AttributeKey,
          videoAttributes,
        ),
        value: attribute.selectedValues.attributeName,
      });

      if (attribute.selectedValues.attributeName) {
        const attributeName =
          attribute.selectedValues.attributeName as AttributeKey;

        attribute.valuesList =
          videoAttributes.mappings[attributeName]?.values.map((item) => ({
            key: item?.label || item.name,
            value: item.name,
          })) || [];
      }
    });

    return {
      addedAttributes,
      selectedAttributeValueKeys,
    };
  }

  updateAttributeFilterSelection(
    filters: FilteredVideoListModule['filters'],
    source: string,
    optionType: 'Group' | 'Value',
    selectedIds: string[],
  ): FilteredVideoListModule['filters'] {
    if (!Array.isArray(filters)) {
      return filters;
    }

    return filters.map((filter) => {
      if ((filter as FiltersAttributeDefinition).source !== source) {
        return filter;
      }

      return optionType === 'Group'
        ? {
            source,
            groups: [...selectedIds],
          }
        : {
            source,
            values: [...selectedIds],
          };
    }) as FilteredVideoListModule['filters'];
  }

  resetAttributeFilter(
    filters: FilteredVideoListModule['filters'],
    source: string,
    optionType: 'Group' | 'Value',
  ): FilteredVideoListModule['filters'] {
    return this.updateAttributeFilterSelection(
      filters,
      source,
      optionType,
      [],
    );
  }

  mapSelectedFilters(
    filters: FilteredVideoListModule['filters'],
    filterOptions: Map<string, FiltersAttributeDefinition['source']>,
  ): MappedFilter[] {
  if (!filters || !Array.isArray(filters)) {
    return [];
  }

  const seenSources = new Set<string>();

  return filters.reduce((result, filter) => {
    const source = (filter).source;

    if (seenSources.has(source)) {
      return result;
    }

    seenSources.add(source);

    const attributeFilter = filter as FiltersAttributeDefinition & {
      groups?: string[];
      values?: string[];
    };

    const isAttribute = source.startsWith('attr');

    let optionType: 'Group' | 'Value' = 'Group';
    if (isAttribute && !attributeFilter.groups) {
      optionType = 'Value';
    }

    const selectedOptionIds = isAttribute
      ? [...(attributeFilter.groups || attributeFilter.values || [])]
      : [];

    const labelEntry = Array.from(filterOptions.entries()).find(
      ([, value]) => value === source,
    );

    result.push({
      label: labelEntry?.[0] || source,
      source,
      isAttribute,
      optionType,
      imageUrl: null,
      selectedOptionIds,
    });

    return result;
  }, [] as MappedFilter[]);
}

  async getAttributes(channelId: string, attributeKey?: string): Promise<VideoAttributesMapping> {
    const settings = await firstValueFrom(this.consentTerms.getChannelSettingsWithFallback(channelId));

    if (!settings || Object.keys(settings).length === 0) {
      return {} as VideoAttributesMapping;
    }

    if (attributeKey) {
      const specificMapping = getVideoAttributesMapping(settings)?.mappings?.[attributeKey as AttributeKey];
      return { mappings: { [attributeKey]: specificMapping } } as VideoAttributesMapping;
    }

    return (getVideoAttributesMapping(settings) || {}) as VideoAttributesMapping;
  }

  async updateAttribute(updatedData: AttributeProps): Promise<void> {
    const writeAttribute = httpsCallable(this.functions, 'channel-writeattribute');
    await writeAttribute(updatedData);
  }

  async uploadAttributeIcon(
    channelId: string,
    {
      imageBuffer,
      fileType,
      attributeName,
    }: {
      fileType?: string;
      imageBuffer?: string;
      attributeName?: string;
    },
    attributeData: VideoAttribute
  ): Promise<VideoAttribute> {
    if (fileType && channelId && imageBuffer) {
      const path = `channels/${channelId}/settings/attributes/${attributeName}`;
      const fileName = `Channel-${channelId}-${attributeName}.${fileType.split('/')[1]}`;
      attributeData.icon = await this.uploadIcons(fileType, path, imageBuffer, fileName);
    }
    return attributeData;
  }

  async uploadValueIcons(
    attributeValues: (VideoAttributeValue & { id?: number; imageFile?: File | null })[],
    channelId: string,
    attributeName: string
  ): Promise<VideoAttributeValue[]> {
    await Promise.all(
      attributeValues.map(async (value, index: number) => {
        if (value.imageFile && channelId) {
          const path = `channels/${channelId}/settings/attributes/${attributeName}`;
          const fileName = `Channel-${channelId}-${attributeName}-${value.name}${index}.${value.imageFile.type.split('/')[1]}`;
          attributeValues[index].icon = await this.uploadIcons(
            value.imageFile.type,
            path,
            value.icon,
            fileName
          );
        }
        delete value.imageFile;
      })
    );
    return attributeValues;
  }

  async uploadIcons(
    fileType: string,
    path: string,
    image: string | undefined,
    fileName: string
  ): Promise<string> {
    this.imageUpload = new Upload(image || '');
    this.imageUpload.name = fileName;
    this.imageUpload.metadata = { contentType: fileType };
    const task = await this.uploadService.pushUpload(this.imageUpload, path);
    const url = await getDownloadURL(task.ref);
    return url;
  }

  saveNamedTags(
    channelId: string,
    namedTagsData: Partial<VideoAttributesMapping['useNamedTagAsAttribute']>,
    addAttributes: boolean
  ): Promise<void> {
    const settingsDocRef = doc(this.firestore, 'channels', channelId, 'settings', 'public');
    return setDoc(
      settingsDocRef,
      {
        attributesMapping: {
          ...(addAttributes ? { mappings: {} } : {}),
          useNamedTagAsAttribute: namedTagsData ?? deleteField(),
        },
      },
      { merge: true }
    );
  }
}
